import type { GoEntityCardFieldValue } from "@/types/go-entity-card";

export type ClassifierTreeNode = {
  /** Префикс пути с точками, напр. `1.1.` или `__other__.` */
  path: string;
  label: string;
  children: ClassifierTreeNode[];
  /** Поля, чей field_key точно равен path (лист-узел). */
  ownFields: GoEntityCardFieldValue[];
};

export type RootProgress = {
  path: string;
  label: string;
  filled: number;
  total: number;
  percent: number;
};

export type ClassifierProgress = {
  filled: number;
  total: number;
  percent: number;
  roots: RootProgress[];
};

export function normalizeFieldKey(key: string): string {
  const t = (key || "").trim();
  if (!t) return "";
  return t.endsWith(".") ? t : `${t}.`;
}

export function fieldKeySegments(key: string): string[] {
  return normalizeFieldKey(key)
    .split(".")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function prefixAt(segments: string[], depth: number): string {
  return `${segments.slice(0, depth).join(".")}.`;
}

export function isFieldFilled(fv: GoEntityCardFieldValue): boolean {
  if (fv.is_masked || fv.is_na) return true;
  const v = fv.value;
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
}

/** Поля, которые считаются «дырами» для прогресса и Continue. */
export function isFillGap(fv: GoEntityCardFieldValue): boolean {
  if (fv.is_masked || fv.is_na) return false;
  return !isFieldFilled(fv);
}

type MutableNode = {
  path: string;
  label: string;
  children: Map<string, MutableNode>;
  ownFields: GoEntityCardFieldValue[];
};

function ensureChild(parent: MutableNode, path: string, labelHint: string): MutableNode {
  let node = parent.children.get(path);
  if (!node) {
    node = { path, label: labelHint, children: new Map(), ownFields: [] };
    parent.children.set(path, node);
  } else if (node.label === path && labelHint && labelHint !== path) {
    node.label = labelHint;
  }
  return node;
}

function finalize(node: MutableNode): ClassifierTreeNode {
  const children = [...node.children.values()]
    .map(finalize)
    .sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  return {
    path: node.path,
    label: node.label,
    children,
    ownFields: node.ownFields,
  };
}

/**
 * Дерево по префиксам field_key. Не-префиксные/пустые ключи → ветка «Прочее».
 */
export function buildClassifierTree(fields: GoEntityCardFieldValue[]): ClassifierTreeNode[] {
  const root: MutableNode = {
    path: "",
    label: "",
    children: new Map(),
    ownFields: [],
  };
  const other: MutableNode = {
    path: "__other__.",
    label: "Прочее",
    children: new Map(),
    ownFields: [],
  };

  for (const fv of fields) {
    const segs = fieldKeySegments(fv.field_key);
    if (segs.length === 0 || !/^\d/.test(segs[0] ?? "")) {
      other.ownFields.push(fv);
      continue;
    }
    let cur = root;
    for (let d = 1; d <= segs.length; d++) {
      const path = prefixAt(segs, d);
      const isLeaf = d === segs.length;
      const labelHint = isLeaf ? fv.label || path : path;
      cur = ensureChild(cur, path, labelHint);
      if (isLeaf) {
        cur.ownFields.push(fv);
        if (fv.label) cur.label = fv.label;
      }
    }
  }

  const tops = [...root.children.values()].map(finalize).sort((a, b) =>
    a.path.localeCompare(b.path, undefined, { numeric: true }),
  );
  if (other.ownFields.length > 0 || other.children.size > 0) {
    tops.push(finalize(other));
  }
  return tops;
}

export function collectDescendantFields(node: ClassifierTreeNode): GoEntityCardFieldValue[] {
  const out: GoEntityCardFieldValue[] = [...node.ownFields];
  for (const ch of node.children) {
    out.push(...collectDescendantFields(ch));
  }
  return out;
}

export function findNodeByPath(
  roots: ClassifierTreeNode[],
  path: string,
): ClassifierTreeNode | null {
  const target = normalizeFieldKey(path);
  const walk = (nodes: ClassifierTreeNode[]): ClassifierTreeNode | null => {
    for (const n of nodes) {
      if (n.path === target) return n;
      const hit = walk(n.children);
      if (hit) return hit;
    }
    return null;
  };
  return walk(roots);
}

/** Путь предков от корня до поля (включая leaf path). */
export function ancestorPathsForField(fieldKey: string): string[] {
  const segs = fieldKeySegments(fieldKey);
  if (segs.length === 0) return ["__other__."];
  if (!/^\d/.test(segs[0] ?? "")) return ["__other__."];
  const paths: string[] = [];
  for (let d = 1; d <= segs.length; d++) {
    paths.push(prefixAt(segs, d));
  }
  return paths;
}

export function computeProgress(fields: GoEntityCardFieldValue[]): ClassifierProgress {
  // masked и N/A — не дыры: не в знаменателе to-fill
  const fillable = fields.filter((f) => !f.is_masked && !f.is_na);
  const filled = fillable.filter((f) => isFieldFilled(f)).length;
  const total = fillable.length;
  const percent = total === 0 ? 100 : Math.round((filled / total) * 100);

  const roots = buildClassifierTree(fields);
  const rootProgress: RootProgress[] = roots.map((r) => {
    const desc = collectDescendantFields(r).filter((f) => !f.is_masked && !f.is_na);
    const f = desc.filter((x) => isFieldFilled(x)).length;
    const t = desc.length;
    return {
      path: r.path,
      label: r.label,
      filled: f,
      total: t,
      percent: t === 0 ? 100 : Math.round((f / t) * 100),
    };
  });

  return { filled, total, percent, roots: rootProgress };
}

/** Первый gap в порядке field_key (numeric). */
export function findFirstEmptyField(
  fields: GoEntityCardFieldValue[],
): GoEntityCardFieldValue | null {
  const sorted = [...fields].sort((a, b) =>
    normalizeFieldKey(a.field_key).localeCompare(normalizeFieldKey(b.field_key), undefined, {
      numeric: true,
    }),
  );
  return sorted.find((f) => isFillGap(f)) ?? null;
}

export function filterTreeByQuery(
  roots: ClassifierTreeNode[],
  query: string,
): ClassifierTreeNode[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return roots;

  const filterNode = (node: ClassifierTreeNode): ClassifierTreeNode | null => {
    const selfHit =
      node.path.toLowerCase().includes(needle) ||
      node.label.toLowerCase().includes(needle) ||
      node.ownFields.some(
        (f) =>
          f.field_key.toLowerCase().includes(needle) ||
          (f.label || "").toLowerCase().includes(needle),
      );
    const children = node.children
      .map(filterNode)
      .filter((n): n is ClassifierTreeNode => n != null);
    const ownFields = selfHit
      ? node.ownFields
      : node.ownFields.filter(
          (f) =>
            f.field_key.toLowerCase().includes(needle) ||
            (f.label || "").toLowerCase().includes(needle),
        );
    if (!selfHit && children.length === 0 && ownFields.length === 0) return null;
    return { ...node, children, ownFields: ownFields.length ? ownFields : node.ownFields };
  };

  return roots.map(filterNode).filter((n): n is ClassifierTreeNode => n != null);
}
